/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { CommonApplication, CommonApplicationGroup, CommonScreentime } from '@codidae/common-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Button from 'components/button/button';
import Flex from 'components/flex/flex';
import { useMemo, useState } from 'react';
import { BiCheckCircle, BiChevronDown, BiChevronRight } from 'react-icons/bi';
import { setAltusAppGroups } from 'services/childrens';
import { getScreentimeForPeriod } from 'services/screentime';
import styles from './app-group-selector.module.scss';

interface P {
  childrenId: string;
  defaultSelectedGroups?: number[];
  defaultSelectedApps?: number[];
  onBack: () => void;
}

export function AppGroupSelector({ childrenId, onBack, defaultSelectedApps, defaultSelectedGroups }: P) {
  const [selectedGroups, setSelectedGroups] = useState<Record<string, boolean>>(
    defaultSelectedGroups ? defaultSelectedGroups.reduce((acc, groupId) => ({ ...acc, [groupId]: true }), {}) : {},
  );
  const [selectedApps, setSelectedApps] = useState<Record<string, boolean>>(
    defaultSelectedApps ? defaultSelectedApps.reduce((acc, appId) => ({ ...acc, [appId]: true }), {}) : {},
  );
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  const queryClient = useQueryClient();

  const setAltusAppGroupMutation = useMutation({
    mutationFn: setAltusAppGroups,
    onSuccess: () => {
      onBack();
      queryClient.invalidateQueries({ queryKey: ['children', childrenId] });
    },
  });

  const lastMonth = new Date();
  lastMonth.setMonth(lastMonth.getMonth() - 1);
  const { data: screentimes } = useQuery({
    queryKey: ['getScreentimeForPeriod', childrenId],
    queryFn: () => getScreentimeForPeriod(Number(childrenId), lastMonth, new Date()),
  });

  // merge all screentimes into one array
  const mergedScreentimes = screentimes?.reduce((acc, screentime) => {
    const { screentimes: sc } = screentime;
    if (sc) {
      return [...acc, ...sc];
    }
    return acc;
  }, [] as CommonScreentime[]);

  // group by group, and then by app inside
  const groupedScreentimes = useMemo(
    () =>
      mergedScreentimes?.reduce(
        (acc, screentime) => {
          const group = screentime.application.groups[0]?.id;
          const app = screentime.application.id;
          if (!acc[group]) {
            acc[group] = {};
          }
          if (!acc[group][app]) {
            acc[group][app] = screentime.application;
          }
          return acc;
        },
        {} as Record<string, Record<string, CommonApplication>>,
      ),
    [mergedScreentimes],
  );

  const groups = useMemo(
    () =>
      mergedScreentimes?.reduce(
        (acc, screentime) => {
          const group = screentime.application.groups[0]?.id;
          if (!acc[group]) {
            acc[group] = screentime.application.groups[0];
          }
          return acc;
        },
        {} as Record<string, CommonApplicationGroup>,
      ),
    [mergedScreentimes],
  );

  const handleGroupSelection = (groupId: string) => {
    const isSelected = !selectedGroups[groupId];

    setSelectedGroups((prev) => ({
      ...prev,
      [groupId]: isSelected,
    }));

    // Select/deselect all apps in the group
    const groupApps = groupedScreentimes?.[groupId] || {};

    const updatedApps = { ...selectedApps };
    Object.keys(groupApps).forEach((appId) => {
      updatedApps[appId] = isSelected;
    });

    setSelectedApps(updatedApps);
  };

  const handleAppSelection = (appId: number) => {
    setSelectedApps((prev) => ({
      ...prev,
      [appId]: !prev[appId],
    }));
  };

  const toggleGroupExpansion = (groupId: string, event: React.MouseEvent) => {
    event.preventDefault();
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  // Determine if a group has some but not all apps selected
  const isGroupPartiallySelected = (groupId: string): boolean => {
    if (selectedGroups[groupId]) return false; // If fully selected, not partially selected

    const groupApps = groupedScreentimes?.[groupId] || {};
    const appIds = Object.keys(groupApps);

    if (appIds.length === 0) return false;

    const selectedCount = appIds.filter((appId) => selectedApps[appId]).length;
    return selectedCount > 0 && selectedCount < appIds.length;
  };

  return (
    <>
      <Flex
        width="100%"
        direction="column"
        gap={15}
        height="100%"
        pad={20}
        style={{
          overflow: 'auto',
        }}
      >
        <div className={styles.header}>Séléction des applications</div>
        <Flex width="100%" direction="column" gap={10} className={styles.weekSettings}>
          {Object.entries(groupedScreentimes || {}).map(([group, apps]) => (
            <div key={group} className={styles.groupContainer}>
              <div className={styles.groupHeader} onClick={(e) => toggleGroupExpansion(group, e)}>
                <div className={styles.groupHeaderContent}>
                  <div className={styles.expandIcon}>
                    {expandedGroups[group] ? <BiChevronDown size={20} /> : <BiChevronRight size={20} />}
                  </div>
                  <label className={styles.checkbox} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedGroups[group] || false}
                      onChange={() => handleGroupSelection(group)}
                      className={isGroupPartiallySelected(group) ? styles.indeterminate : ''}
                      ref={(input) => {
                        if (input) {
                          input.indeterminate = isGroupPartiallySelected(group);
                        }
                      }}
                    />
                    <h3>
                      {groups?.[group]?.name} (
                      {
                        // get number of selected apps in group or if group is selected, get all apps
                        selectedGroups[group]
                          ? Object.values(apps).length
                          : Object.values(apps).filter((app) => selectedApps[app.id]).length
                      }
                      /{Object.values(apps).length})
                    </h3>
                  </label>
                </div>
              </div>
              {expandedGroups[group] && (
                <ul className={styles.appList}>
                  {Object.values(apps).map((app) => (
                    <li key={app.id} className={styles.appItem}>
                      <label className={styles.checkbox}>
                        <input
                          type="checkbox"
                          checked={selectedGroups[group] || selectedApps[app.id] || false}
                          onChange={() => handleAppSelection(app.id)}
                          disabled={selectedGroups[group] || false}
                        />
                        <div className={styles.appInfo}>
                          <div className={styles.appIcon}>
                            {app.icon && <img src={`data:image/png;base64,${app.icon}`} alt={app.name} />}
                          </div>
                          <span
                            style={{
                              maxWidth: 'calc(100vw - 150px)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {app.name}
                          </span>
                        </div>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </Flex>
      </Flex>
      <Flex
        gap={15}
        direction="column"
        width="100%"
        pad={15}
        style={{ background: 'var(--primary100)', borderTop: 'solid 3px var(--primary200)' }}
      >
        <Button
          onClick={() => {
            const appsInSelectedGroups = new Set<number>();

            Object.keys(selectedGroups)
              .filter((groupId) => selectedGroups[groupId])
              .forEach((groupId) => {
                // Get all apps in this group
                const groupApps = groupedScreentimes?.[groupId] || {};
                Object.values(groupApps).forEach((app) => {
                  appsInSelectedGroups.add(app.id);
                });
              });

            // Filter out applications that are already included in selected groups
            const individualSelectedApps = Object.keys(selectedApps)
              .filter((appId) => selectedApps[appId] && !appsInSelectedGroups.has(Number(appId)))
              .map((appId) => Number(appId));

            setAltusAppGroupMutation.mutate({
              id: Number(childrenId),
              applications: individualSelectedApps,
              groups: Object.keys(selectedGroups)
                .filter((groupId) => selectedGroups[groupId])
                .map((groupId) => Number(groupId)),
            });
          }}
          loading={setAltusAppGroupMutation.isPending}
          icon={<BiCheckCircle />}
        >
          VALIDER LES APPS
        </Button>
        <Button
          onClick={() => {
            onBack();
          }}
          outline
        >
          RETOUR
        </Button>
      </Flex>
    </>
  );
}
